/**
 * Computes the difference between two strings based on lines.
 *
 * @param {string} oldText Original text
 * @param {string} newText New text
 * @returns {Array} Array of change objects { count: number, added?: boolean, removed?: boolean, value?: string }
 */
export const diffLines = (oldText, newText) => {
    // Normalize line endings
    const oldLines = oldText
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n");
    const newLines = newText
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n");

    // DP matrix for LCS length
    const m = oldLines.length;
    const n = newLines.length;

    // Use a flat array or typed array if performance is an issue, but for code files it should be fine.
    // However, for large files O(N*M) is slow.
    // Optimization: Trim common prefix/suffix.

    let start = 0;
    while (start < m && start < n && oldLines[start] === newLines[start]) {
        start++;
    }

    let endOld = m - 1;
    let endNew = n - 1;

    while (
        endOld >= start &&
        endNew >= start &&
        oldLines[endOld] === newLines[endNew]
    ) {
        endOld--;
        endNew--;
    }

    const prefix = [];
    if (start > 0) {
        prefix.push({ count: start, added: false, removed: false });
    }

    if (start > endOld && start > endNew) {
        return prefix;
    }

    const suffixCount = m - 1 - endOld;

    // Compute LCS on the middle part
    const middleOld = oldLines.slice(start, endOld + 1);
    const middleNew = newLines.slice(start, endNew + 1);

    const mm = middleOld.length;
    const nn = middleNew.length;

    // Use 1D array for space optimization? No, need path reconstruction.
    // Since we are likely dealing with small edits, this is acceptable.

    const C = Array(mm + 1)
        .fill(0)
        .map(() => Array(nn + 1).fill(0));

    for (let i = 1; i <= mm; i++) {
        for (let j = 1; j <= nn; j++) {
            if (middleOld[i - 1] === middleNew[j - 1]) {
                C[i][j] = C[i - 1][j - 1] + 1;
            } else {
                C[i][j] = Math.max(C[i][j - 1], C[i - 1][j]);
            }
        }
    }

    // Backtrack
    let i = mm;
    let j = nn;
    const middleDiff = [];

    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && middleOld[i - 1] === middleNew[j - 1]) {
            middleDiff.unshift({ count: 1, added: false, removed: false });
            i--;
            j--;
        } else if (j > 0 && (i === 0 || C[i][j - 1] >= C[i - 1][j])) {
            middleDiff.unshift({ count: 1, added: true, removed: false });
            j--;
        } else {
            middleDiff.unshift({ count: 1, added: false, removed: true });
            i--;
        }
    }

    // Compress consecutive chunks
    const result = [...prefix];

    middleDiff.forEach((part) => {
        if (
            result.length > 0 &&
            result[result.length - 1].added === part.added &&
            result[result.length - 1].removed === part.removed
        ) {
            result[result.length - 1].count += part.count;
        } else {
            result.push(part);
        }
    });

    if (suffixCount > 0) {
        if (
            result.length > 0 &&
            !result[result.length - 1].added &&
            !result[result.length - 1].removed
        ) {
            result[result.length - 1].count += suffixCount;
        } else {
            result.push({ count: suffixCount, added: false, removed: false });
        }
    }

    return result;
};
